import React, { useState, useEffect } from "react";
import Header from "./Header.js";
import Footer from "./Footer.js";
import Select from "react-select";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./Style.css";
import { TailSpin } from "react-loader-spinner";
import { Link } from "react-router-dom";

// Import Artwork Components
import Add_Art_Work from "../../Components/instructor_comp/add_artwork.js";
import Update_Art_Work from "../../Components/instructor_comp/update_artwork.js";
import Delete_Art_Work from "../../Components/instructor_comp/delete_artwork.js";

// API URLs
import { getArtWorksByUserId, getArtworkLimit, API_URL } from "../../Api/apiUrls.js";

function Instructor_Artwork() {
  // State Management
  const [artworkData, setArtworkData] = useState([]);
  const [filteredArtworkData, setFilteredArtworkData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Filters & Search
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  
  // Artwork Limits
  const [artworkLimit] = useState(9); // Maximum allowed active artworks
  const [activeArtworkCount, setActiveArtworkCount] = useState(0); // Count of active artworks
  const [canAddMore, setCanAddMore] = useState(true);
  
  // Modal States
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Selected Artwork Data
  const [selectedArtwork, setSelectedArtwork] = useState(null);

  // Search Handler
  const handleSearchInputChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Filter Data
  useEffect(() => {
    const filteredData = artworkData.filter((artwork) => {
      const isFilter =
        !selectedFilter ||
        selectedFilter.value === "All" ||
        artwork.filter === selectedFilter.value;
      const isStatus =
        !selectedStatus ||
        selectedStatus.value === "All" ||
        artwork.status === selectedStatus.value;
      const isMonth =
        !selectedMonth ||
        selectedMonth.value === "All" ||
        artwork.created_at_month === selectedMonth.value;
      const isYear =
        !selectedYear ||
        selectedYear.value === "All" ||
        artwork.created_at_year === selectedYear.value;

      const values = Object.values(artwork).map((value) =>
        typeof value === "string" ? value.toLowerCase() : ""
      );
      const isSearchMatch = values.some((value) =>
        value.includes(searchQuery.toLowerCase())
      );
      return isFilter && isStatus && isMonth && isYear && isSearchMatch;
    });

    setFilteredArtworkData(filteredData);
  }, [
    selectedFilter,
    selectedStatus,
    selectedYear,
    selectedMonth,
    artworkData,
    searchQuery,
  ]);

  // Format Date
  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return "N/A";
    const createdAtDate = new Date(createdAt);
    return createdAtDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Count active artworks
  const countActiveArtworks = (artworks) => {
    return artworks.filter(artwork => 
      artwork.status === "Active" || artwork.status_code === 1
    ).length;
  };

  // Fetch Artwork Data
  const fetchArtworkData = async () => {
    setIsLoading(true);
    const instructorId = localStorage.getItem("user_id");
    
    try {
      // Fetch Artwork Data
      const response = await fetch(`${getArtWorksByUserId}${instructorId}`);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      const artwork = data.data?.ArtWorks || [];
      
      // Transform data to match expected format
      const formattedArtwork = artwork.map(item => ({
        id: item.id,
        name: item.title || "Untitled Artwork",
        description: item.description || "",
        price: item.rate || "0",
        category: item.category || { name: "Uncategorized" },
        // Map status codes to status text
        status: item.status === 1 ? "Active" : item.status === 0 ? "Pending" : "Inactive",
        status_code: item.status || 0, // Keep the original status code
        image: item.photo ? `${API_URL}/media/${item.photo}` : null,
        sequence: item.sequence || "0",
        created_at: item.created_at || new Date().toISOString()
      }));
      
      setArtworkData(formattedArtwork);
      
      // Count active artworks
      const activeCount = countActiveArtworks(formattedArtwork);
      setActiveArtworkCount(activeCount);
      
      // Check if user can add more (limit is 9 active artworks)
      setCanAddMore(activeCount < artworkLimit);
      
    } catch (error) {
      console.error("Error fetching artwork data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchArtworkData();
  }, []);

  // Modal Handlers
  const openAddModal = () => {
    if (!canAddMore) {
      alert(`You have reached the maximum limit of ${artworkLimit} ACTIVE artworks.\n\nYou currently have ${activeArtworkCount} active artworks. Please:\n1. Deactivate some artworks, OR\n2. Upgrade your package to add more.`);
      return;
    }
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
  };

  const openEditModal = (artwork) => {
    setSelectedArtwork(artwork);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setSelectedArtwork(null);
    setShowEditModal(false);
  };

  const openDeleteModal = (artwork) => {
    setSelectedArtwork(artwork);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setSelectedArtwork(null);
    setShowDeleteModal(false);
  };

  // Handle successful operations
  const handleArtworkAdded = () => {
    fetchArtworkData();
    closeAddModal();
  };

  const handleArtworkUpdated = () => {
    fetchArtworkData();
    closeEditModal();
  };

  const handleArtworkDeleted = () => {
    fetchArtworkData();
    closeDeleteModal();
  };

  // Filter Options
  const filter = [
    { value: "All", label: "All" },
    { value: "Featured", label: "Featured" },
    { value: "Sponsored", label: "Sponsored" },
  ];

  const month = [
    { value: "All", label: "All" },
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const year = [
    { value: "All", label: "All" },
    { value: "2021", label: "2021" },
    { value: "2022", label: "2022" },
    { value: "2023", label: "2023" },
    { value: "2024", label: "2024" },
    { value: "2025", label: "2025" },
  ];

  const status = [
    { value: "All", label: "All" },
    { value: "Active", label: "Active" },
    { value: "Pending", label: "Pending" },
    { value: "Inactive", label: "Inactive" },
  ];

  // Display Limit Message
  const getLimitMessage = () => {
    if (activeArtworkCount >= artworkLimit) {
      return `You have reached your limit of ${artworkLimit} ACTIVE artworks. You currently have ${activeArtworkCount} active artworks. Deactivate some artworks or upgrade your package to add more.`;
    }
    return `You can add ${artworkLimit - activeArtworkCount} more ACTIVE artworks (${activeArtworkCount}/${artworkLimit} active artworks used)`;
  };

  // Get total artworks count for display
  const getTotalArtworksCount = () => {
    const totalCount = artworkData.length;
    const pendingCount = artworkData.filter(item => item.status === "Pending" || item.status_code === 0).length;
    const inactiveCount = artworkData.filter(item => item.status === "Inactive" || item.status_code === -1).length;
    
    return {
      total: totalCount,
      active: activeArtworkCount,
      pending: pendingCount,
      inactive: inactiveCount
    };
  };

  const counts = getTotalArtworksCount();

  return (
    <div>
      <Header />
      
      {/* Modals */}
      {showAddModal && (
        <Add_Art_Work 
          onClose={closeAddModal} 
          onArtworkAdded={handleArtworkAdded}
          instructorId={localStorage.getItem("user_id")}
        />
      )}
      
      {showEditModal && selectedArtwork && (
        <Update_Art_Work
          artwork={selectedArtwork}
          onClose={closeEditModal}
          onArtworkUpdated={handleArtworkUpdated}
        />
      )}
      
      {showDeleteModal && selectedArtwork && (
        <Delete_Art_Work
          artwork={selectedArtwork}
          onClose={closeDeleteModal}
          onArtworkDeleted={handleArtworkDeleted}
        />
      )}

      <main id="main" className="main">
        <div className="pagetitle">
          <h1>Artwork Management</h1>
          <div className={`alert ${activeArtworkCount >= artworkLimit ? 'alert-danger' : 'alert-info'} mt-2`} role="alert">
            <i className="bi bi-info-circle me-2"></i>
            {getLimitMessage()}
          </div>
        </div>

        <section className="section">
          <div className="card p-3">
            {/* Search and Filter Section */}
            <div className="row mb-3">
              <div className="col-md-3 mb-2">
                <input
                  type="text"
                  className="form-control"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  placeholder="Search artwork..."
                />
              </div>
              
              <div className="col-md-2 mb-2">
                <Select
                  options={status}
                  placeholder="Select Status"
                  onChange={setSelectedStatus}
                  isClearable
                />
              </div>
              
              <div className="col-md-2 mb-2">
                <button
                  className="btn btn-primary w-100"
                  onClick={openAddModal}
                  disabled={!canAddMore}
                  title={!canAddMore ? "Active artwork limit reached" : "Add new artwork"}
                >
                  <i className="bi bi-plus-circle"></i> Add Artwork
                </button>
              </div>
            </div>

            {/* Loading State */}
            {isLoading ? (
              <div className="text-center py-5">
                <TailSpin color="#0d6efd" height={50} width={50} />
                <p className="mt-2">Loading artworks...</p>
              </div>
            ) : (
              /* Artwork Table */
              <div className="table-responsive">
                {filteredArtworkData.length > 0 ? (
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Image</th>
                        <th scope="col">Artwork Name</th>
                        <th scope="col">Description</th>
                        <th scope="col">Rate</th>
                        <th scope="col">Sequence</th>
                        <th scope="col">Status</th>
                        <th scope="col">Created On</th>
                        <th scope="col">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredArtworkData.map((artwork, index) => (
                        <tr key={artwork.id}>
                          <th scope="row">{index + 1}</th>
                          <td>
                            {artwork.image ? (
                              <img
                                src={artwork.image}
                                alt={artwork.name}
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                  borderRadius: "5px",
                                }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "https://via.placeholder.com/60x60?text=No+Image";
                                }}
                              />
                            ) : (
                              <div className="text-muted">
                                <i className="bi bi-image" style={{ fontSize: "30px" }}></i>
                              </div>
                            )}
                          </td>
                          <td>
                            <strong>{artwork.name}</strong>
                          </td>
                          <td>
                            <small className="text-muted">
                              {artwork.description && artwork.description.length > 50
                                ? `${artwork.description.substring(0, 50)}...`
                                : artwork.description || "No description"}
                            </small>
                          </td>
                          <td>
                            <span className="fw-bold">
                              ₹{parseFloat(artwork.price || 0).toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-secondary">
                              {artwork.sequence || "0"}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                artwork.status === "Active"
                                  ? "bg-success"
                                  : artwork.status === "Pending"
                                  ? "bg-warning"
                                  : "bg-danger"
                              }`}
                            >
                              {artwork.status || "Inactive"}
                            </span>
                          </td>
                          <td>
                            <small>{formatCreatedAt(artwork.created_at)}</small>
                          </td>
                          <td>
                            <div className="btn-group" role="group">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => openEditModal(artwork)}
                                title="Edit"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => openDeleteModal(artwork)}
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-palette display-1 text-muted"></i>
                    <h4 className="mt-3">No Artworks Found</h4>
                    <p className="text-muted">
                      {searchQuery
                        ? "No artworks match your search criteria"
                        : "You haven't added any artworks yet"}
                    </p>
                    {canAddMore && (
                      <button
                        className="btn btn-primary mt-2"
                        onClick={openAddModal}
                      >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add Your First Artwork
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Summary Footer */}
            {!isLoading && filteredArtworkData.length > 0 && (
              <div className="mt-3 pt-3 border-top">
                <div className="row">
                  <div className="col-md-6">
                    <p className="text-muted mb-0">
                      Showing {filteredArtworkData.length} of {artworkData.length} artworks
                    </p>
                  </div>
                  <div className="col-md-6 text-end">
                    <div className="alert alert-warning mb-0 py-2" style={{ display: 'inline-block' }}>
                      <small>
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Active Artworks: {activeArtworkCount}/{artworkLimit}
                        <br />
                        <small className="text-muted">
                          (Total: {counts.total}, Pending: {counts.pending}, Inactive: {counts.inactive})
                        </small>
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}

export default Instructor_Artwork;

